/**
 * InsufficientCreditsModal — shown when API returns 402 or optimistic check fails.
 */

import { useCreditsStore, CREDIT_PACKS } from '../store/creditsStore';

interface Props {
  action: string;
  required: number;
  available: number;
  onClose: () => void;
}

export default function InsufficientCreditsModal({ required, available, onClose }: Props) {
  const { checkout } = useCreditsStore();
  const needed = required - available;

  const handleBuy = async (packId: string) => {
    try {
      await checkout(packId);
    } catch {
      // checkout redirects to Stripe
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="r-modal-body"
        style={{
          background: '#13141A', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 20, padding: '32px 28px', maxWidth: 520, width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚡</div>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 8 }}>
            You need {needed} more credit{needed !== 1 ? 's' : ''}
          </h3>
          <p style={{ fontSize: 14, color: '#6b7280' }}>
            Your balance: <span style={{ color: '#ef4444', fontWeight: 600 }}>{available}</span>
            {' '}· Required: <span style={{ color: '#fff', fontWeight: 600 }}>{required}</span>
          </p>
        </div>

        {/* Pack grid */}
        <div className="r-grid-2" style={{ gap: 12, marginBottom: 20 }}>
          {CREDIT_PACKS.map(pack => (
            <div
              key={pack.id}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${pack.bestValue ? 'rgba(249,115,22,0.5)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 12, padding: 16, position: 'relative',
                textAlign: 'center',
              }}
            >
              {pack.bestValue && (
                <div style={{
                  position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
                  background: '#f97316', borderRadius: 10, padding: '2px 10px',
                  fontSize: 9, fontWeight: 800, color: '#fff', letterSpacing: '0.05em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  BEST VALUE
                </div>
              )}
              <div style={{ fontSize: 20, marginBottom: 4 }}>{pack.emoji}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                {pack.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, color: '#f97316', marginBottom: 2 }}>
                {pack.credits}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>credits</div>
              <div style={{ fontSize: 11, color: '#6b7280', textDecoration: 'line-through', marginBottom: 2 }}>
                ${pack.regularPrice.toFixed(2)}
              </div>
              <button
                onClick={() => handleBuy(pack.id)}
                style={{
                  width: '100%', padding: '8px 0', borderRadius: 8,
                  background: pack.bestValue ? '#f97316' : 'rgba(255,255,255,0.06)',
                  border: pack.bestValue ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                ${pack.betaPrice.toFixed(2)}
              </button>
            </div>
          ))}
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          style={{
            display: 'block', width: '100%', textAlign: 'center',
            background: 'none', border: 'none', color: '#6b7280',
            fontSize: 13, cursor: 'pointer', padding: 8,
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
